import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { backdrop, panel, tap } from "../motion";
import { IconX } from "./icons";
import { icpStatusLabel } from "../hooks/useResidents";
import { useResidentDetail, type ResidentDetail } from "../hooks/useIcpFlow";
import { ResidentStatusPanel } from "./ResidentStatusPanel";
import { IcpHistoryCard } from "./IcpHistoryCard";

// Cross-home resident quick-view — opened via /residents/:id (global search
// deep link or a Residents card). A read-only snapshot of the live detail the
// GET /api/residents/:id endpoint returns; the ICP action delegates to the
// guided flow (Stage B) rather than duplicating state-machine buttons.

function QuickViewBody({ detail, onOpenFlow, onOpenIcpHistory }: { detail: ResidentDetail; onOpenFlow: () => void; onOpenIcpHistory: () => void }) {
  return (
    <>
      <div className="modal-body">
        <div className="todo-meta">
          <span className={`chip ${detail.status === "discharged" ? "chip-danger" : detail.status === "hospital" ? "chip-warn" : ""}`}>{detail.status}</span>
          {detail.icpFillStatus ? <span className={`chip ${detail.icpFillStatus === "filled" ? "chip-ok" : detail.icpFillStatus === "form_available" ? "chip-accent" : "chip-warn"}`}>{icpStatusLabel(detail.icpFillStatus)}</span> : null}
          {detail.overdue ? <span className="chip chip-danger">Overdue</span> : detail.dueSoon ? <span className="chip chip-warn">Due soon</span> : null}
          {detail.workWindowToday ? <span className="chip chip-accent">Do this half-month</span> : null}
        </div>
        <dl className="quickview-facts">
          <div><dt>Admitted</dt><dd>{detail.admissionDate ?? "—"}</dd></div>
          <div><dt>Last Ax</dt><dd>{detail.lastAxDate ?? "—"}</dd></div>
        </dl>
        <motion.button type="button" className="btn btn-secondary" {...tap} onClick={onOpenIcpHistory}>ICP History</motion.button>
        <ResidentStatusPanel detail={detail} />
        {detail.deepLink ? (
          <a href={detail.deepLink} target="_blank" rel="noopener noreferrer">Open the sheet row →</a>
        ) : null}
        {detail.icpFillStatus !== "filled" && (detail.status === "new" || detail.status === "active") ? (
          <motion.button type="button" className="btn btn-primary" {...tap} onClick={onOpenFlow}>Continue ICP flow</motion.button>
        ) : null}
      </div>
    </>
  );
}

export function ResidentQuickView({ residentId, onClose, onOpenFlow }: { residentId: string | null; onClose: () => void; onOpenFlow: () => void }) {
  const detail = useResidentDetail(residentId);
  const [showIcp, setShowIcp] = useState(false);

  useEffect(() => {
    if (!residentId || showIcp) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [residentId, showIcp, onClose]);

  // Closing the card when the resident changes keeps the overlay in sync.
  useEffect(() => { setShowIcp(false); }, [residentId]);

  return (
    <>
      <AnimatePresence>
      {residentId ? (
        <motion.div className="palette-backdrop" role="dialog" aria-modal="true" aria-label="Resident quick view" onClick={onClose} variants={backdrop} initial="hidden" animate="show" exit="exit">
          <motion.div className="modal" onClick={(e) => e.stopPropagation()} variants={panel} initial="hidden" animate="show" exit="exit">
            <div className="modal-head">
              <h2>{detail.data ? detail.data.name : "Resident"}</h2>
              {detail.data?.bed ? <span className="chip">{detail.data.bed}</span> : null}
              {detail.data?.homeName ? <span className="chip">{detail.data.homeName}</span> : null}
              <button type="button" className="btn btn-icon btn-ghost" aria-label="Close" onClick={onClose}><IconX /></button>
            </div>
            {detail.isLoading ? <p className="muted" style={{ padding: "var(--space-4)" }}>Loading…</p> : null}
            {detail.isError ? <p className="error" style={{ padding: "var(--space-4)" }}>Could not load this resident. Try again.</p> : null}
            {detail.data ? <QuickViewBody detail={detail.data} onOpenFlow={onOpenFlow} onOpenIcpHistory={() => setShowIcp(true)} /> : null}
          </motion.div>
        </motion.div>
      ) : null}
      </AnimatePresence>
      {detail.data ? <IcpHistoryCard detail={detail.data} open={showIcp} onClose={() => setShowIcp(false)} /> : null}
    </>
  );
}
